import {
  parsePositionKey,
  RogPositionResultCamel,
} from '../pages/ground-truth/components/rogStatusUtils';

const buildKey = (mod: number, shelf: number, column: number) => `${mod},${shelf},${column}`;

export const buildPositionKey = buildKey;

/**
 * Remove the position at (mod, shelf, removedColumn) and shift every higher
 * column on the same shelf down by one. Other shelves and mods are untouched.
 */
export const removeRogPosition = (
  rogResultsByPosition: Record<string, RogPositionResultCamel>,
  mod: number,
  shelf: number,
  removedColumn: number
): Record<string, RogPositionResultCamel> => {
  const next: Record<string, RogPositionResultCamel> = {};
  for (const [key, value] of Object.entries(rogResultsByPosition)) {
    const parsed = parsePositionKey(key);
    if (!parsed || parsed.mod !== mod || parsed.shelf !== shelf) {
      next[key] = value;
      continue;
    }
    if (parsed.column === removedColumn) continue;
    if (parsed.column > removedColumn) {
      next[buildKey(mod, shelf, parsed.column - 1)] = value;
    } else {
      next[key] = value;
    }
  }
  return next;
};

/**
 * Move a position from (fromMod, fromShelf, fromColumn) to (toMod, toShelf, toColumn).
 * Preserves the value at the source slot. Source shelf is shifted down (remove
 * semantics), then the destination shelf is shifted up to make room (add
 * semantics). Source and destination shelves can be the same.
 */
export const moveRogPosition = (
  rogResultsByPosition: Record<string, RogPositionResultCamel>,
  fromMod: number,
  fromShelf: number,
  fromColumn: number,
  toMod: number,
  toShelf: number,
  toColumn: number
): Record<string, RogPositionResultCamel> => {
  const sourceKey = buildKey(fromMod, fromShelf, fromColumn);
  const value = rogResultsByPosition[sourceKey];
  if (!value) return rogResultsByPosition;
  if (fromMod === toMod && fromShelf === toShelf && fromColumn === toColumn) {
    return rogResultsByPosition;
  }
  const afterRemove = removeRogPosition(rogResultsByPosition, fromMod, fromShelf, fromColumn);
  return addRogPosition(afterRemove, toMod, toShelf, toColumn, value);
};

/**
 * Insert a new position at (mod, shelf, newColumn), shifting any existing entry
 * at that column (and higher) up by one to make room.
 */
export const addRogPosition = (
  rogResultsByPosition: Record<string, RogPositionResultCamel>,
  mod: number,
  shelf: number,
  newColumn: number,
  value: RogPositionResultCamel
): Record<string, RogPositionResultCamel> => {
  const next: Record<string, RogPositionResultCamel> = {};
  for (const [key, existing] of Object.entries(rogResultsByPosition)) {
    const parsed = parsePositionKey(key);
    if (!parsed || parsed.mod !== mod || parsed.shelf !== shelf) {
      next[key] = existing;
      continue;
    }
    if (parsed.column >= newColumn) {
      next[buildKey(mod, shelf, parsed.column + 1)] = existing;
    } else {
      next[key] = existing;
    }
  }
  next[buildKey(mod, shelf, newColumn)] = value;
  return next;
};
